import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import ToolDetailsContent from "views/components/ToolDetailsContent";
import { ToolInfo } from "views/components/ToolInfo";
import { ToolAnalytics } from "views/components/ToolAnalytics";
import AlternativeTools from "views/components/AlternativeTools";
import { ToolNav } from "views/components/ToolNav";
import { Layout } from "views/common/Layout";
import { Breadcrumbs } from "views/components/Breadcrumbs";
import { fetchTool } from "actions/tool";
import { Loader2 } from "lucide-react";

const Tool = ({ tool, fetchTool }) => {
    const { name } = useParams();

    useEffect(() => {
        if (name) {
            fetchTool(name);
        }
    }, [name, fetchTool]);

    if (!tool) {
        return (
            <Layout>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex justify-center items-center min-h-[50vh]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Helmet>
                <title>{`${tool.name} - ${
                    tool.shortDescription || "Tool Details"
                } | Launch Hero FYI`}</title>
                <meta
                    name="description"
                    content={
                        tool.description ||
                        `Learn more about ${tool.name}, its features, analytics, and alternatives. Find the perfect AI tool for your needs.`
                    }
                />
                <meta
                    name="keywords"
                    content={`${tool.name}, ${tool.category}, AI tool, ${
                        tool.tags?.join(", ") || ""
                    }, alternative tools`}
                />
                <meta
                    property="og:title"
                    content={`${tool.name} - ${
                        tool.shortDescription || "AI Tool Details"
                    } | Launch Hero FYI`}
                />
                <meta
                    property="og:description"
                    content={
                        tool.description ||
                        `Learn more about ${tool.name}, its features, analytics, and alternatives. Find the perfect AI tool for your needs.`
                    }
                />
                <meta property="og:type" content="product" />
                <meta property="og:url" content={window.location.href} />
                {tool.image && (
                    <meta property="og:image" content={tool.image} />
                )}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={`${tool.name} - ${
                        tool.shortDescription || "Tool Details"
                    } | Launch Hero FYI`}
                />
                <meta
                    name="twitter:description"
                    content={
                        tool.description ||
                        `Learn more about ${tool.name}, its features, analytics, and alternatives. Find the perfect AI tool for your needs.`
                    }
                />
                {tool.image && (
                    <meta name="twitter:image" content={tool.image} />
                )}
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            {/* Tool Banner Section */}
            <div className="relative bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="relative">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-4">
                        {/* Breadcrumbs */}
                        <div className="mb-6 relative z-10 backdrop-blur-sm rounded-lg p-2 inline-block mt-10">
                            <Breadcrumbs
                                items={[
                                    {
                                        label: tool.category
                                            .split("|")[0]
                                            .trim(),
                                        href: `/category`,
                                    },
                                    {
                                        label: tool.category
                                            .split("|")[1]
                                            ?.trim(),
                                        href: tool.category.split("|")[1]
                                            ? `/category/${tool.category
                                                  .split("|")[1]
                                                  .trim()}`
                                            : undefined,
                                    },
                                    {
                                        label: tool.name,
                                    },
                                ].filter((item) => item.label)}
                            />
                        </div>
                        <div id="overview" className="pt-4">
                            <ToolDetailsContent tool={tool} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                <div>
                    <div className="pt-2">
                        <div
                            id="features"
                            className="mt-16 pt-16 border-t border-gray-200"
                        >
                            <ToolInfo tool={tool} />
                        </div>
                        {/*<div
                            id="analytics"
                            className="mt-16 pt-16 border-t border-gray-200"
                        >
                            <ToolAnalytics tool={tool} />
                        </div>*/}
                    </div>
                </div>
            </div>

            {/* Alternatives Section */}
            <div className="w-full bg-blue-50 dark:bg-gray-900">
                <div
                    id="alternatives"
                    className="pt-8 border-t border-gray-200 dark:border-gray-700 pb-16"
                >
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <AlternativeTools tool={tool} />
                    </div>
                </div>
            </div>
            <ToolNav />
        </Layout>
    );
};

const mapStateToProps = (state, ownProps) => {
    const { name } = ownProps.match.params;
    return {
        tool: state.tool.toolsMap[name],
    };
};

export default connect(mapStateToProps, { fetchTool })(Tool);
